import React, { useRef, useEffect, useState } from "react";
import { useContext, useCallback, useMemo } from "react";
import { useField, useFormikContext } from "formik";
import Field from "../UI/forms/Field";
import Checkbox from "../UI/forms/Checkbox";
import { Box } from "reflexbox";
import Card from "../UI/Card";
import { Body } from "../UI/Text";
import Radio from "../UI/forms/Radio";
import Range from "../UI/forms/Range";
import * as constants from "../../modules/constants";
import { configure } from "../../requests/apiRequests";
import Button from "../UI/Button";
import ConfiguratorContext from "../../context/configurator/ConfiguratorContext";
import UserContext from "../../context/user/UserContext";
import { useTheme } from "styled-components";
import { useParams } from "react-router-dom";
//import RichText from "../UI/RichText";

const { StayOnScreenRule, StayOnTab, MoveTabAndScreenRule } =
  constants.AUTO_CONTINUE_MODE;

const FormField = ({
  ScreenOption,
  animateIndex,
  lastOptionIndex,
  tooltip,
}) => {
  const theme = useTheme();
  const { productId } = useParams();

  const {
    IsRequired,
    ID,
    IsEnabled,
    Name: name,
    SelectableValues,
    Ranges,
    DisplayFormat,
    DisplayType,
    Caption,
    AutoContinueMode,
    IsVisible,
    Value,
  } = ScreenOption || {};  

  const Name = useMemo(() => {
    return name.replace('["', "_").replace('"]', "");
  }, [name]);

  const { Increment, Min, Max } = Ranges?.[0] || {};

  const type = DisplayType?.toLowerCase();
  const caption = Caption?.replace(/<\/?[^>]+(>|$)/g, "");

  const [field] = useField(Name);

  const { platform } = useContext(UserContext);
  const { requireHttps } = platform || {};

  const { optionToFocus, setApi, api, setOptionToFocus, sessionId, setACMode } =
    useContext(ConfiguratorContext);

  useEffect(() => {
        if (optionToFocus === ID && !field.value) {
      if (!!ref.current?.focus) {
     // ref.current.focus();
      //console.log(ref);
       // ref.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    }
  }, [ID, optionToFocus, field.value]);

  const runSubmit = useCallback(
    async (event, { value }) => {

      // check if the value is a react-select value
      const reactSelectValue = event?.value;  

      if (!reactSelectValue && event) event.stopPropagation();    			

      const type = DisplayType.toLowerCase();
      if (type === constants.CHECKBOX) value = value ? "True" : "False";
      if (type === constants.NUMERIC_TEXTBOX) value = `${value}`;
      
      const _res = await configure({
        sessionId,
        id: ID || sessionId,
        value: value || "",
        requireHttps,
        productId,
      });
      setApi(_res?.data);

      setOptionToFocus(_res?.data?.d?.OptionToFocus);
    },
    [
      DisplayType,
      ID,
      productId,
      requireHttps,
      sessionId,
      setApi,
      setOptionToFocus,
    ]
  );

  const ref = useRef(null);
  const { setFieldValue } = useFormikContext();

  const [shouldSubmit, setShouldSubmit] = useState(null);

 const throttle = useRef(null);

  useEffect(() => { 
    switch (shouldSubmit) {        
      case 0:        
                clearTimeout(throttle.current);
        throttle.current = null;
        setShouldSubmit(1);
        break;
      case 1:        
                if (throttle.current) {
          if (document.activeElement.id === ref.current.id) {
            setShouldSubmit(0);
          }
        } else {          
          const timeout = setTimeout(() => {
            clearTimeout(throttle.current);

            setShouldSubmit(2);
          }, 2000);
          throttle.current = timeout;
        }
        break;
      case 2:
        // if (type === constants.NUMERIC_CONTROL) {
        //   runSubmit({ value: decimalToFraction(field.value) });
        // }
        runSubmit(null, { value: field.value });
        clearTimeout(throttle.current);        
setShouldSubmit(null);
        break;
            default:        
        throttle.current = null;
        break;
    }
  }, [runSubmit, shouldSubmit, field.value]);

  const onChange = useCallback(
    (e) => {         
      const reactSelectValue = e?.value;      
      // hack to support react-select typeable dropdown      
      if (reactSelectValue) {                   
        setFieldValue(Name, reactSelectValue);   
        if (reactSelectValue === "")  {                
          return;
        }    
        runSubmit(e, { value : reactSelectValue });       
        return;
      } else {
        const val = e?.target?.value;        
        setFieldValue(Name, val);     
        if (e?.target?.value === "")  {                
          return;
        }

        if (shouldSubmit === 1) setShouldSubmit(0);
        else setShouldSubmit(1);
      }
    },
    [Name, setFieldValue, runSubmit, shouldSubmit]
  );

  const onBlur = useCallback(
    (e) => {               
        const val = e?.target?.value;        
        setFieldValue(Name, val);       
        runSubmit(e, {value: val});
    },
    [Name, runSubmit, setFieldValue]
  );

  const fieldProps = useMemo(() => {
    const ret = {
      required: IsRequired,
      disabled: !!!IsEnabled,
      autoFocus:
        optionToFocus === ID && !api?.d?.IsExecutionComplete && !field.value,
      name: Name,
      onFocus: (e) => {
        setACMode(AutoContinueMode);
        setOptionToFocus(ID);
      },
      labelText: caption,
      id: ID,          
      onChange: (e) => setFieldValue(field.name, e.target.value),
      ref,
      tooltip,
      checkedValue: field.value || Value,
      ...field,
    };    

    switch (AutoContinueMode) {
      case StayOnScreenRule:
        // Do nothing
        // OR
        // Test if this will move to the next question
        // If true, save locally then submit on tab change
        ret.onBlur = onChange;

        break;
      case StayOnTab:
        // Scroll to next line 
        ret.onBlur = onChange;
        break;
      case MoveTabAndScreenRule:
        // Check if the last screenOption on the next page
        // is the one that has focus then move the page
        ret.onChange = onChange;
        break;
      default:
        break;
    }
    return ret;
  }, [
    AutoContinueMode,
    ID,
    IsEnabled,
    IsRequired,
    Name,
    api,
    Value,
    caption,
    field,
    onChange,
    optionToFocus,
    setACMode,
    setFieldValue,
    setOptionToFocus,
    tooltip,
  ]);

  const radioProps = useMemo(
    () => ({
      innerRef: ref,
      animateIndex,
      lastOptionIndex,
      checkedValue: field.value,
    }),
    [animateIndex, lastOptionIndex, field.value]
  );

  const options = useMemo(() => {
    const opts = [];

    for (let val of SelectableValues) {      
      if (!val.IsVisible) break;
      opts.push(
        <option
          disabled={!val.IsEnabled || !IsEnabled}
          key={val.Value}
          value={val.Value}          
        >
          {val.Caption}
        </option>
      );
    }

    return opts;
  }, [IsEnabled, SelectableValues]);

  const reactSelectOptions = useMemo(() => {
    return SelectableValues.filter(val => val.IsVisible).map(val => ({
      value: val.Value,
      label: val.Caption,
      isDisabled: !val.IsEnabled || !IsEnabled,
    }));
  }, [SelectableValues, IsEnabled]);
  

  if (!IsVisible) return null;

  switch (type) {
    case constants.DROPDOWN:
      return (
        <Field as="select" {...fieldProps}>
          <option value="" key="default">
            Select a Value
          </option>
          {options}
        </Field>
      );

    case constants.RADIO_HORIZTONAL:
      return (
        <div key={Name}>
          <Radio
            {...radioProps}
            {...fieldProps}
                        options={SelectableValues.map((val) => ({
              value: val.Value,
              label: (
                <Button
                  className={field.value === val.Value ? "checked" : ""}
                  reverse={val.Value !== field.value}
                  onClick={(e) => {
                    setFieldValue(field.name, val.Value);
                    runSubmit(e, {
                      value: val.Value,
                    });
                  }}
                  disabled={!val.IsEnabled || !!!IsEnabled}
                  style={{ display: val.IsVisible ? undefined : "none" }}
                  color={
                    val.Value !== field.value ? theme.gray : theme.secondary
                  }
                >
                  {val.Caption}
                </Button>
              ),
            }))}
          />
        </div>
      );
    case constants.RADIO_VERTICAL:
      return (
        <div key={Name}>
          <Radio
            {...radioProps}
            {...fieldProps}
                        options={SelectableValues.map((val) => ({
              value: val.Value,
              label: (
                <Button
                  className={field.value === val.Value ? "checked" : ""}
                  reverse={val.Value !== field.value}
                  onClick={(e) =>
                    runSubmit(e, {
                      value: val.Value,
                    })
                  }
                  disabled={!val.IsEnabled || !!!IsEnabled}
                  style={{ display: val.IsVisible ? undefined : "none" }}
                  color={
                    val.Value !== field.value ? theme.grayDark : theme.secondary
                  }
                >
                  {val.Caption}
                </Button>
              ),
            }))}
          />
        </div>
      );

    case constants.NUMERIC_TEXTBOX:
      let _fv = type === "number" ? field.value.replace(",", "") : field.value;
      return (
        <Field
          readOnly={_fv === "Display_Caption"}
          type="number"
                    {...fieldProps}
        />
      );

    case constants.TEXTBOX:
      let formattedValue =
        type === "number" ? field.value.replace(",", "") : field.value;

      return (
        <Field
          readOnly={formattedValue === "Display_Caption"}
          {...fieldProps}
          />
      );
  case constants.TEXTAREA:
    // let formattedValue =
    // type === "number" ? field.value.replace(",", "") : field.value;

  return (
    <Field
      readOnly={formattedValue === "Display_Caption"}
      {...fieldProps}
    />
  );
      // let fv = type === "number" ? field.value.replace(",", "") : field.value;

      // return (
      //   <RichText
      //     disabled={fv === "Display_Caption"}
      //     {...{ ...fieldProps, onChange: undefined }}
          //     onChange={undefined}
          //     runSubmit={runSubmit}                   
        //   />
      // );

    case constants.CHECKBOX:
      return (
        <Box my="0.5rem">
          <Checkbox
            {...fieldProps}
            value={field.value}
            onChange={() => {}}
            onClick={(e) => {
              setFieldValue(field.name, !field.value);
              return runSubmit(e, {
                value: !field.value,
              });
            }}
          />
        </Box>
      );

    case constants.IMAGE_TEXT_HORIZONTAL:
      return (
        <Radio
          {...radioProps}
          optionWidth={100 / SelectableValues.length}
          {...fieldProps}
          options={SelectableValues.map((val) => ({
            value: val.Value,
            label: (
              <Card
                className={field.value === val.Value ? "checked" : ""}
                style={{ display: val.IsVisible ? undefined : "none" }}
                width={1}
                key={val.Value}
                isRadio
                active={field.value === val.Value}
                interactive
                onClick={(e) => {
                  setFieldValue(field.name, val.Value);
                  return !val.IsEnabled || !!!IsEnabled
                    ? undefined
                    : runSubmit(e, {
                        value: val.Value,
                      });
                }}
              >
                <img src={val.ImageLink} alt={val.Caption} />
                <Body center mt="1rem">
                  {val.Caption}
                </Body>
              </Card>
            ),
          }))}
        />
      );
    case constants.IMAGE_TEXT_WRAP:
      return (
        <Radio
          {...radioProps}
          optionWidth={100 / SelectableValues.length}
          {...fieldProps}
          options={SelectableValues.map((val) => ({
            value: val.Value,
            label: (
              <Card
                className={field.value === val.Value ? "checked" : ""}
                style={{ display: val.IsVisible ? undefined : "none" }}
                width={1}
                key={val.Value}
                isRadio
                active={field.value === val.Value}
                interactive
                onClick={(e) =>
                  !val.IsEnabled || !!!IsEnabled
                    ? undefined
                    : runSubmit(e, {
                        value: val.Value,
                      })
                }
              >
                <img
                  src={
                    val.ImageLink?.startsWith("http://20.232.187.103")
                      ? val.ImageLink.replace("http://20.232.187.103", "")
                      : val.ImageLink
                  }
                  alt={val.Caption}
                />
                <Body center mt="1rem">
                  {val.Caption}
                </Body>
              </Card>
            ),
          }))}
        />
      );

    case constants.TYPEABLE_DROPDOWN:             
      return (
        <Field
          {...fieldProps}
          typeableSelect
          reactSelectOptions={reactSelectOptions}
                                            
        />
            );

    case constants.NUMERIC_CONTROL:
      return (
        <Range
          unit={`"`}
          isFraction={DisplayFormat === "Fraction"}
          step={Increment}
          min={Min}
          max={Max}
          {...fieldProps}
                  />
      );

    case constants.IMAGE_DROPDOWN:
      return (
        <Radio
          {...radioProps}
          {...fieldProps}
          optionWidth={100 / SelectableValues.length}
          options={SelectableValues.map((val) => ({
            value: val.Value,
            label: (
              <Card
                style={{ display: val.IsVisible ? undefined : "none" }}
                width={1}
                key={val.Value}
                isRadio
                className={field.value === val.Value ? "checked" : ""}
                active={field.value === val.Value}
                interactive
                onClick={(e) => {
                  if (!val.IsEnabled || !!!IsEnabled) return undefined;
                  setFieldValue(field.name, val.Value);
                  return runSubmit(e, {
                    value: val.Value,
                  });
                }}
              >
                <img
                  src={
                    val.ImageLink?.startsWith("http://20.232.187.103")
                      ? val.ImageLink.replace("http://20.232.187.103", "")
                      : val.ImageLink
                  }
                  alt={val.Caption}
                />
                <Body center mt="1rem">
                  {val.Caption}
                </Body>
              </Card>
            ),
          }))}
        />
      );
    default:
      return null;
  }
};

export default FormField;
